import { useCallback, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import API from "../api";
import { colors } from "../theme";
import { useOrion } from "../contexts/OrionContext";
import OrionModuleDialog from "./OrionModuleDialog";
import { startToast, updateToastError, updateToastSuccess } from "../utils/notification-loading";

// Pulsante di attivazione/disattivazione del modulo Orion, indipendente da
// dove viene montato -- legge/scrive lo stato condiviso in OrionContext (la
// stessa sorgente di verità usata da Sidebar, AssetToolBar e le route guard),
// così Inventory e Network Overview possono renderlo entrambe accanto al
// proprio titolo senza duplicare stato locale o rischiare di disallinearsi.
const OrionToggleButton = () => {
  const { orionEnabled, setOrionEnabled } = useOrion();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("enable");

  // Prima di proporre l'attivazione si chiede al server se il LECS può
  // ospitare il modulo (Zeek fermo e pacchetto Orion assente => no).
  const handleOpen = useCallback(() => {
    if (orionEnabled) {
      setDialogMode("disable");
      setConfirmOpen(true);
      return;
    }
    API.getOrionVisibility()
      .then((res) => setDialogMode(res?.data?.moduleInstallable === false ? "unavailable" : "enable"))
      .catch(() => setDialogMode("enable"))
      .finally(() => setConfirmOpen(true));
  }, [orionEnabled]);

  const handleConfirm = useCallback(() => {
    setConfirmOpen(false);
    const next = !orionEnabled;

    startToast();
    API.setOrionVisibility({ visibility: next })
      .then(() => {
        updateToastSuccess("Successfull");
        setOrionEnabled(next);
      })
      .catch((err) => {
        updateToastError(err);
      });
  }, [orionEnabled, setOrionEnabled]);

  return (
    <>
      <Tooltip
        title={
          orionEnabled
            ? "ORION disable — analysis columns hidden"
            : "ORION enable — analysis columns visible (TYPE, OS, RISK, CVEs)"
        }
      >
        <Box
          component="button"
          onClick={handleOpen}
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.9,
            px: 1.8,
            py: 0.85,
            border: "1px solid",
            borderColor: orionEnabled ? "rgba(140,110,255,0.55)" : colors.grey[800],
            borderRadius: "999px",
            cursor: "pointer",
            background: orionEnabled
              ? "linear-gradient(135deg, rgba(140,110,255,0.22), rgba(76,206,172,0.12))"
              : "rgba(255,255,255,0.03)",
            boxShadow: orionEnabled ? "0 0 14px rgba(140,110,255,0.35)" : "none",
            transition: "all .2s ease",
            "&:hover": {
              borderColor: "rgba(140,110,255,0.9)",
              boxShadow: "0 0 18px rgba(140,110,255,0.5)",
            },
          }}
        >
          <AutoAwesomeIcon
            sx={{
              fontSize: 17,
              color: orionEnabled ? "#c3b3ff" : colors.grey[600],
              filter: orionEnabled ? "drop-shadow(0 0 4px rgba(195,179,255,0.8))" : "none",
              transition: "all .2s ease",
            }}
          />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.16em",
              color: orionEnabled ? "#ece7ff" : colors.grey[500],
            }}
          >
            ORION
          </Typography>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: orionEnabled ? "#4cceac" : colors.grey[700],
              boxShadow: orionEnabled ? "0 0 6px #4cceac" : "none",
              transition: "all .2s ease",
            }}
          />
        </Box>
      </Tooltip>

      <OrionModuleDialog
        open={confirmOpen}
        mode={dialogMode}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default OrionToggleButton;
